// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
/**
 * What the Computer speed test card says under its score (owner decision, October 6, 2026:
 * approach A, "Two sides", from the speed-advice design study). "Set by this computer" names
 * the hardware with this computer's own value beside each part; "Up to you" names what the
 * person can do, each with one plain line and, where it applies, the button that does it.
 *
 * Every line restates something the app does (findings.md, Verified facts): the graphics
 * card does the two big reading steps when Speed boost is on; the processor does all of it
 * when there is no Speed boost; learned skills answer before the slow step; training runs on
 * its schedule while nobody uses the computer; free graphics memory decides how Speed boost
 * starts; Paige holds back while someone uses the computer. The one number is a
 * measurement and says so ("Measured · …"). Nothing here promises a speed-up.
 *
 * Pure and dependency-free, so the node checks read the shipped words.
 *
 * After a speed test, Free graphics memory names the heavy apps that held it when the test started
 * (Brandy, October 6, 2026), from main's one reading (graphics-memory-users.ts), and only where
 * closing them would change what the engine does: read more at once, or start Speed boost at all.
 * Everyday apps (browsers, email, chat, Office) and Windows are never named or asked to close:
 * "everyone's going to have Chrome running… that's not realistic." The test itself measures the
 * computer as it is normally used, so nothing asks anyone to close anything before it.
 */
import type { GraphicsMemoryAtTestStart, ReaderMemoryPlan } from '../shared/desktop-api';

export type SpeedBoostMode = 'on' | 'partial' | 'attention' | 'off' | 'unknown';

export type SpeedAdviceFacts = {
  /** Speed boost on this computer (speedBoostSummary). */
  boost: SpeedBoostMode;
  /** Graphics adapters found on this computer; [] when the check found none; null when not known. */
  cards: readonly string[] | null;
  /** Not enough graphics memory was free when the document reader started. */
  memoryShort: boolean;
  /** The processor and memory recorded by this computer's latest speed test; null before one. */
  processor: string | null;
  memoryMegabytes: number | null;
  /** The job the person is working in; null with no job. */
  job: { id: string; settingUp?: boolean } | null;
  /** That job's skills, as Learning counts them; null until read. `unknown` were not reported. */
  skills: { ready: number; total: number; unknown?: number } | null;
  /** Training on a schedule on this computer; null until read. `start`/`end`: its hours ("21:00"), when on. */
  training: {
    on: boolean;
    window: string | null;
    start?: string | null;
    end?: string | null;
  } | null;
  /** Graphics memory when this session's latest speed test started; null or absent before one. */
  graphicsMemory?: GraphicsMemoryAtTestStart | null;
};

export type AdviceActionId = 'teach' | 'train' | 'speed-boost';
export type AdviceTone = 'on' | 'off' | 'soft' | 'warn' | '';
export type AdviceRowId =
  'card' | 'processor' | 'memory' | 'teach' | 'train' | 'free-memory' | 'away';
export type AdviceRow = {
  id: AdviceRowId;
  name: string;
  /** This computer's (or this job's) own value, shown beside the name; null when not known. */
  value: string | null;
  tone: AdviceTone;
  /** One plain line; absent where the value says enough (Memory). */
  line?: string;
  action?: { id: AdviceActionId; label: string };
  /** A quiet web link after the line, opened in the browser. */
  link?: { label: string; href: string };
};
export type SpeedAdvice = { computer: AdviceRow[]; yours: AdviceRow[] };

/** The help article that shows, step by step, which apps use the graphics card (help.ademero.com). */
export const GRAPHICS_MEMORY_GUIDE_URL =
  'https://help.ademero.com/capturepoint-6/performance/whats-using-my-graphics-card';

/**
 * How many documents the reader takes at once with this much graphics memory free when it starts,
 * as the engine decides it. With the engine's own plan for this card (the reading's `readerPlan`,
 * from its reader-memory-plan command: owner items 122 and 145), its steps: that carries an
 * AMD/Intel card's widths and the extra a reader with flash attention off needs. Without one (an
 * engine from before it), the NVIDIA figures: ResourceBudget.GpuSemanticSlotsFor (4 from 14 GiB,
 * 2 from 10 GiB, otherwise 1), never wider than WidestReaderSlotsThatFit allows, and 0, the
 * processor, when not even one reader fits (ReaderReserveMegabytesFor(1), 5,500 + 1,280 MiB).
 */
export const MOST_AT_ONCE = 4;
export function documentsAtOnce(freeMegabytes: number, plan?: ReaderMemoryPlan | null): number {
  const steps = usableSteps(plan);
  if (steps)
    return steps.reduce(
      (slots, step) => (freeMegabytes >= step.freeMegabytes ? step.slots : slots),
      0,
    );
  if (freeMegabytes >= 14 * 1024) return 4;
  if (freeMegabytes >= 10 * 1024) return 2;
  return freeMegabytes >= 5500 + 1280 ? 1 : 0;
}
/** The most documents at once this card's reader ever takes: the plan's widest step, else MOST_AT_ONCE. */
export function mostAtOnce(plan?: ReaderMemoryPlan | null): number {
  return usableSteps(plan)?.at(-1)?.slots ?? MOST_AT_ONCE;
}
/** A plan's steps when they can be used: growing in both width and memory, every number whole and positive. */
function usableSteps(plan: ReaderMemoryPlan | null | undefined): ReaderMemoryPlan['steps'] | null {
  const steps = plan?.steps;
  if (!Array.isArray(steps) || steps.length === 0) return null;
  const whole = (value: unknown): value is number =>
    Number.isSafeInteger(value) && (value as number) > 0;
  return steps.every(
    (step, index) =>
      whole(step?.slots) &&
      whole(step?.freeMegabytes) &&
      (index === 0 ||
        (step.slots > steps[index - 1]!.slots &&
          step.freeMegabytes > steps[index - 1]!.freeMegabytes)),
  )
    ? steps
    : null;
}

/**
 * An app is named only from this much graphics memory, all its windows together: about 1 GB, what
 * games, video, photo and 3D editors, screen recorders and other AI tools hold. Main never sends
 * everyday apps or Windows by name (graphics-memory-users.ts), so those are never among them.
 */
export const HEAVY_APP_MEGABYTES = 1024;
const MOST_NAMES = 3;
/** Before a speed test, and wherever its reading adds nothing. */
export const FREE_MEMORY_LINE =
  'Apps holding graphics memory when reading starts can make Speed boost start smaller, or not at all.';
const gigabytes = (megabytes: number) =>
  `${(megabytes / 1024).toLocaleString('en-US', { maximumFractionDigits: 1 })} GB`;
const nameList = (names: string[], others: boolean) =>
  others
    ? `${names.join(', ')} and others`
    : names.length < 2
      ? names.join('')
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export type GraphicsMemoryNote = {
  /** The reader took fewer documents at once than this card allows, because of what else held its memory. */
  short: boolean;
  /** Closing the heavy apps it names would let the reader take more at once, or start on the card at all. */
  fixable: boolean;
  line: string;
};

/**
 * What the reading at the speed test's start adds to Free graphics memory, or null when it adds
 * nothing (no reading, no free figure, or the most at once already fit), by the engine's own
 * thresholds (documentsAtOnce):
 * - heavy apps, the fewest biggest first, whose closing would cross a threshold: named (fixable);
 * - otherwise, short because of what else was on the card: no names and no ask, and when Windows
 *   and everyday apps held most of it, the calm "Most of it was in use by Windows and everyday apps.";
 * - not short (the card itself sets the width): "No other app was using much of it" when nothing
 *   else held about 1 GB, else nothing to add.
 */
export function graphicsMemoryNote(
  reading: GraphicsMemoryAtTestStart | null | undefined,
): GraphicsMemoryNote | null {
  if (!reading || reading.freeMegabytes === null) return null;
  const free = reading.freeMegabytes;
  const plan = reading.readerPlan;
  const atOnce = (megabytes: number) => documentsAtOnce(megabytes, plan);
  const now = atOnce(free);
  if (now >= mostAtOnce(plan)) return null;
  const heavy = reading.apps
    .filter((app) => app.megabytes >= HEAVY_APP_MEGABYTES)
    .sort((left, right) => right.megabytes - left.megabytes);
  let freed = 0,
    needed = 0;
  for (const app of heavy) {
    if (atOnce(free + freed) > now) break;
    freed += app.megabytes;
    needed += 1;
  }
  if (needed > 0 && atOnce(free + freed) > now) {
    const counted = heavy.slice(0, needed);
    const names = counted.slice(0, MOST_NAMES).map((app) => app.name);
    const one = counted.length === 1;
    const them = one ? names[0] : 'them';
    return {
      short: true,
      fixable: true,
      line:
        `${nameList(names, counted.length > names.length)} ${one ? 'was' : 'were'} using ${gigabytes(freed)} when it started. ` +
        (now === 0
          ? `Closing ${them} lets Speed boost start.`
          : `Closing ${them} before you open Paige lets it read more at once.`),
    };
  }
  if (atOnce(free + reading.othersMegabytes) > now) {
    // What main did not name: Windows and everyday apps (and the small tail of the rest).
    const windowsAndEveryday =
      reading.othersMegabytes - reading.apps.reduce((sum, app) => sum + app.megabytes, 0);
    return {
      short: true,
      fixable: false,
      line:
        windowsAndEveryday * 2 >= reading.othersMegabytes
          ? 'Most of it was in use by Windows and everyday apps.'
          : FREE_MEMORY_LINE,
    };
  }
  return reading.othersMegabytes < HEAVY_APP_MEGABYTES
    ? { short: false, fixable: false, line: 'No other app was using much of it when it started.' }
    : null;
}

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;

/** A processor's marketing name without the trademark marks, clock speed and core count. */
export function processorName(model: string | null | undefined): string | null {
  if (!model) return null;
  const name = model
    .replace(/\((?:R|TM|C)\)|[®™]/gi, '')
    .replace(/@\s*[\d.]+\s*GHz/i, '')
    .replace(/\b\d+(?:st|nd|rd|th)\s+Gen\b/i, '')
    .replace(/\b\d+-Core\b/i, '')
    .replace(/\b(?:CPU|Processor)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return name || null;
}

/** Graphics adapter names without the trademark marks, each once. */
export function cardNames(names: readonly string[]): string[] {
  return [
    ...new Set(
      names
        .map((name) =>
          name
            .replace(/\((?:R|TM)\)|[®™]/gi, '')
            .replace(/\s+/g, ' ')
            .trim(),
        )
        .filter(Boolean),
    ),
  ];
}

/** Total memory as people say it: "16 GB". */
export const memoryLabel = (megabytes: number | null): string | null =>
  megabytes !== null && Number.isFinite(megabytes) && megabytes > 0
    ? `${Math.max(1, Math.round(megabytes / 1024))} GB`
    : null;

const minutesOf = (value: string) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};
const clock = (value: string) => {
  const minutes = minutesOf(value);
  return minutes === null
    ? value
    : new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
};

/**
 * Whether this computer trains on a schedule: switched on, held by a job, and a window that is
 * not empty (the same test Learning's Train uses). The window reads "9:00 PM – 5:00 AM".
 */
export function scheduledTraining(
  schedule: { enabled: boolean; start: string; end: string },
  owned: boolean,
): { on: boolean; window: string | null; start: string | null; end: string | null } {
  const on = schedule.enabled && owned && schedule.start !== schedule.end;
  return {
    on,
    window: on ? `${clock(schedule.start)} – ${clock(schedule.end)}` : null,
    start: on ? schedule.start : null,
    end: on ? schedule.end : null,
  };
}

/** Speed boost is not doing the reading here: the processor does all of it. */
const processorReads = (boost: SpeedBoostMode) => boost === 'off' || boost === 'attention';

/**
 * Free graphics memory as the card says it: "enough" or "short" when it started, or null where the
 * row says neither (no card Speed boost uses or tried to, or not known). The speed report email
 * sends only this, never which apps held it.
 */
export function freeGraphicsMemoryState(facts: SpeedAdviceFacts): 'enough' | 'short' | null {
  const row = speedAdvice(facts).yours.find((entry) => entry.id === 'free-memory');
  return !row
    ? null
    : row.value === FREE_MEMORY_SHORT
      ? 'short'
      : row.value === FREE_MEMORY_ENOUGH
        ? 'enough'
        : null;
}
const FREE_MEMORY_SHORT = 'Not enough when it started';
const FREE_MEMORY_ENOUGH = 'Enough when it started';

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function speedAdvice(facts: SpeedAdviceFacts): SpeedAdvice {
  const { boost, memoryShort, skills, training, job } = facts;
  const cards = facts.cards ? cardNames(facts.cards) : null;
  const noneFound = cards !== null && cards.length === 0;
  const seeBoost = { id: 'speed-boost' as const, label: 'See speed boost' };

  const card: AdviceRow = {
    id: 'card',
    name: 'Graphics card',
    value: cards === null ? null : noneFound ? 'None found' : cards.join(' · '),
    tone:
      boost === 'on'
        ? 'on'
        : boost === 'attention'
          ? 'warn'
          : noneFound || boost === 'off'
            ? 'off'
            : '',
    line:
      boost === 'on'
        ? 'The biggest difference, and Speed boost is using it.'
        : boost === 'partial'
          ? 'The biggest difference. Speed boost is partly on.'
          : boost === 'attention'
            ? 'The biggest difference, but Speed boost isn’t on right now.'
            : 'The biggest difference. A high-end card turns on Speed boost by itself.',
    ...(boost === 'partial' || boost === 'attention' ? { action: seeBoost } : {}),
  };
  const processor: AdviceRow = {
    id: 'processor',
    name: 'Processor',
    value: processorName(facts.processor),
    tone: '',
    line:
      boost === 'on'
        ? 'Handles the rest, like barcodes and page previews.'
        : noneFound && boost !== 'partial'
          ? 'With no graphics card, it does all the reading, so it sets the pace.'
          : processorReads(boost)
            ? 'Without Speed boost, it does all the reading, so it sets the pace.'
            : 'Does the reading Speed boost isn’t doing, and the rest.',
  };
  const memory: AdviceRow = {
    // What the engine does with memory, never a claim about Ademero's own test computers: while
    // reading it checks free memory every few seconds and takes fewer pages at once when it runs
    // low (ParallelismController). Owner's wording, October 7, 2026.
    id: 'memory',
    name: 'Memory',
    value: memoryLabel(facts.memoryMegabytes),
    tone: '',
    line: 'Shared with everything you have open. Paige works with what’s free.',
  };

  const canAct = Boolean(job && !job.settingUp);
  // Every skill Ready: Learning's own "Train across the job" milestone.
  const allReady =
    skills !== null && skills.total > 0 && skills.ready >= skills.total && !skills.unknown;
  const teach: AdviceRow = {
    id: 'teach',
    name: 'Teach',
    value:
      job && skills && skills.total > 0
        ? `${skills.ready.toLocaleString('en-US')} of ${plural(skills.total, 'skill')} ready`
        : null,
    tone: allReady ? 'on' : '',
    line: 'Each skill it learns answers before the slow step, so there’s less left to work out.',
    ...(canAct && !allReady
      ? { action: { id: 'teach' as const, label: 'Teach in Documents' } }
      : {}),
  };
  const train: AdviceRow = {
    id: 'train',
    name: 'Train while you’re away',
    value:
      training === null
        ? null
        : training.on
          ? `On${training.window ? ` · ${training.window}` : ''}`
          : 'Off',
    tone: training === null ? '' : training.on ? 'on' : 'soft',
    line: 'Training tests better ways to read your documents and keeps only the ones that win. It works the computer hard, so the schedule runs it while you’re away.',
    ...(canAct && training && !training.on
      ? { action: { id: 'train' as const, label: 'Turn on training' } }
      : {}),
  };
  // Only where there is a card Speed boost uses, or tried to. After a speed test, the heavy apps that
  // held the card when it started, where closing them would change what the engine does. Short only
  // because of Windows and everyday apps is the calm amber: nothing to do.
  const usesCard = Boolean(
    cards && cards.length > 0 && (boost === 'on' || boost === 'partial' || boost === 'attention'),
  );
  const note = usesCard ? graphicsMemoryNote(facts.graphicsMemory) : null;
  const short = memoryShort || note?.short === true;
  const freeMemory: AdviceRow | null = usesCard
    ? {
        id: 'free-memory',
        name: 'Free graphics memory',
        value: short ? FREE_MEMORY_SHORT : boost === 'on' ? FREE_MEMORY_ENOUGH : null,
        tone: memoryShort || note?.fixable ? 'warn' : short ? 'soft' : boost === 'on' ? 'on' : '',
        line: note?.line ?? FREE_MEMORY_LINE,
        ...(memoryShort && !card.action ? { action: seeBoost } : {}),
        link: { label: 'How to check', href: GRAPHICS_MEMORY_GUIDE_URL },
      }
    : null;
  const away: AdviceRow = {
    id: 'away',
    name: 'Step away from big batches',
    value: null,
    tone: '',
    line: 'While you use the computer, Paige holds back so it stays responsive. Step away and it speeds back up.',
  };

  return {
    computer: [card, processor, memory],
    yours: [teach, train, ...(freeMemory ? [freeMemory] : []), away],
  };
}
